import React, {useCallback, useEffect, useMemo, useState} from 'react';
import QuickCreateProjectModal, {QuickCreateTypes} from '../browse-talent/QuickCreateProjectModal';
import {useAuth} from '../../providers/AuthProvider';
import {useAPI} from '../../config/API';
import LoadingSpinner from '../../components/LoadingSpinner';
import ProjectPosition from '../../models/ProjectPosition';
import {InputNumber, Input, Skeleton} from 'antd';

const {TypeTimeline} = ProjectPosition.ChangeOrderTypes;

/**
 * How the recorded work relates to what already exists.
 *
 *   NewProject  a fresh scope and dates — a new project and a new SOW.
 *   ChangeOrder the same work continuing — amends the SOW already in place on a live engagement.
 */
export const RehireModes = {
    NewProject: 'new-project',
    ChangeOrder: 'change-order',
};

/**
 * Whether the talent still has to confirm.
 *
 *   Accelerated the client and talent already settled it. No proposal round-trip: the terms are
 *               recorded as agreed and the engagement goes straight to SOW/Change Order.
 *   Verify      the talent hasn't confirmed yet. Runs the standard Quick Create path — they're
 *               invited, confirm availability and respond to the terms before anything is issued.
 */
export const RehirePaths = {
    Accelerated: 'accelerated',
    Verify: 'verify',
};

const NEW_PROJECT_STEPS = [
    {key: 'start', label: 'Get Started', icon: 'fas fa-handshake'},
    {key: 'basic-info', label: 'Role & Scope', icon: 'fas fa-user-tag'},
    {key: 'timing-and-budget', label: 'Terms', icon: 'fas fa-file-invoice-dollar'},
    {key: 'review', label: 'Review', icon: 'fas fa-check-circle'},
];

const CHANGE_ORDER_STEPS = [
    {key: 'start', label: 'Get Started', icon: 'fas fa-handshake'},
    {key: 'select-engagement', label: 'Select Project', icon: 'fas fa-folder-open'},
    {key: 'change-details', label: 'Change Details', icon: 'fas fa-pen-to-square'},
    {key: 'review', label: 'Review', icon: 'fas fa-check-circle'},
];

const shortName = (talent) => talent?.first_name || talent?.firstNamePlusInitial?.() || 'this talent';

const formatDate = (value) => value ? TB.moment(value).format('MMM D, YYYY') : '—';

const weeksBetween = (from, to) => {
    if (!from || !to) return 0;
    const diff = TB.moment(to).diff(TB.moment(from), 'weeks', true);
    return Math.max(0, Math.ceil(diff));
};

/**
 * The position's most recent cost segment — its current terms.
 *
 * A position accumulates a segment per change order, so the last one is what's in force today and
 * what a new change order is measured against. Mirrors how ChangeOrderItemForm and
 * ChangeOrderItemImpactPanel derive their "current" column.
 */
const currentSegmentFor = (position) => {
    if (!position) return null;

    const segment = TB.last(position.candidate_segments || []);
    if (!segment) return null;

    return {
        ...segment,
        platform_fee: (segment.platform_cost || 0) - (segment.talent_cost || 0),
        total_periods: segment.total_periods || segment.total_hours || 0,
        rate: segment.rate || segment.hourly_rate || 0,
    };
};

/**
 * Builds the Timeline change order item for a position, given the revised end date and the hours
 * being added.
 *
 * Follows ProjectPosition.makeNewChangeOrder(TypeTimeline) for the base values and
 * ChangeOrderItemForm.handleSubmit for the one adjustment that isn't obvious:
 *
 *   total_periods  the hours for THIS candidate's segment after the change.
 *   total_hours    the position's total across every segment. Hours already booked against earlier
 *                  candidates have to be added back on, or the change order would silently erase
 *                  them from the position's total.
 */
const buildTimelineChangeOrderItem = (position, {endedAt, addedHours}) => {
    const segment = currentSegmentFor(position);
    if (!position || !segment) return null;

    const basePeriods = segment.total_periods || 0;
    const newTotalPeriods = basePeriods + (Number(addedHours) || 0);

    // Hours belonging to segments before the current one.
    const previousTotalHours = TB.last(position.change_orders || [])?.new_total_periods
        || position.actual_total_periods
        || basePeriods;
    const previousCandidateHours = (previousTotalHours || 0) - basePeriods;

    const rate = segment.rate || 0;
    const platformRate = segment.platform_rate || 0;

    return {
        type: TypeTimeline,
        position_id: position.id,

        // Timeline change orders take effect immediately — the extension starts now, not at the
        // old end date. Matches makeNewChangeOrder().
        effective_at: new Date(),
        ended_at: endedAt,

        total_periods: newTotalPeriods,
        total_hours: newTotalPeriods + previousCandidateHours,

        // Carried unchanged: a Timeline change moves dates and hours, never the rates.
        rate,
        platform_rate: platformRate,
        percent_markup: segment.percent_markup,

        // Recomputed so the impact panel and the submitted item agree.
        talent_cost: rate * newTotalPeriods,
        platform_cost: platformRate * newTotalPeriods,
        platform_fee: (platformRate * newTotalPeriods) - (rate * newTotalPeriods),
    };
};

/**
 * Reconciles the engagement's contract-to-date totals with the change this order would make.
 *
 * Two different scopes are in play, and conflating them is the easy mistake:
 *
 *   to date   position.contract_value / talent_cost / actual_total_hours — cumulative across every
 *             segment and published change order. These are the same figures the project page
 *             labels "Contract Value To Date" and "Talent Cost To Date", and they are what a reader
 *             means by "current contract terms". The last candidate segment on its own is only the
 *             most recent slice, so showing it understates a position that has already been
 *             amended.
 *
 *   impact    previewItem measured against the current segment. A change order item only ever moves
 *             the segment it is written against, so the delta has to be taken there — exactly as
 *             ChangeOrderItemImpactPanel does.
 *
 * New totals are therefore to-date plus impact, rather than the segment's own new figures.
 */
const deriveChangeTotals = (position, currentSegment, previewItem) => {
    if (!position || !currentSegment || !previewItem) return null;

    const currentHours = TB.parseInt(position.actual_total_hours) || currentSegment.total_periods || 0;
    const currentTalentCost = Number(position.talent_cost) || 0;
    const currentContractValue = Number(position.contract_value) || 0;

    const hourImpact = (previewItem.total_periods || 0) - (currentSegment.total_periods || 0);
    const resourceCostImpact = (previewItem.talent_cost || 0) - (currentSegment.talent_cost || 0);
    const contractValueImpact = (previewItem.platform_cost || 0) - (currentSegment.platform_cost || 0);

    const newTalentCost = currentTalentCost + resourceCostImpact;
    const newContractValue = currentContractValue + contractValueImpact;

    return {
        currentHours,
        currentTalentCost,
        currentContractValue,
        currentPlatformFee: currentContractValue - currentTalentCost,

        hourImpact,
        resourceCostImpact,
        contractValueImpact,
        platformFeeImpact: contractValueImpact - resourceCostImpact,

        newHours: currentHours + hourImpact,
        newTalentCost,
        newContractValue,
        newPlatformFee: newContractValue - newTalentCost,
    };
};

/**
 * RehireTalentModal — "Record a rehire" for an in-network (bring-your-own) talent.
 *
 * Clients bring freelancers they already work with into Wripple as in-network talent. When more
 * work comes up with one of them, they still want Wripple as Employer of Record. This modal
 * records that work, and branches on two questions asked up front:
 *
 *   1. Is it new work, or an extension of something already running?
 *   2. Has the talent already confirmed, or do they still need to?
 *
 * The answers pick the step list and how the backend treats the submission. Everything else — the
 * role/scope, terms, review and submit machinery — is reused from QuickCreateProjectModal through
 * its steps / renderStep / isStepValid extension points, so the two flows can't drift apart.
 *
 * @param {Object} talent      - The talent user being rehired (TalentCandidate)
 * @param {Object} talentPool  - The in-network pool the rehire was initiated from
 */
const RehireTalentModal = ({talent, talentPool, onSuccess, onClose}) => {
    return (
        <QuickCreateProjectModal
            type={QuickCreateTypes.Rehire}
            data={talent}
            talentPool={talentPool}
            // Intentionally empty: a rehire has no search context to pre-fill from.
            searchFilters={{}}
            steps={steps}
            renderStep={renderStep}
            isStepValid={isStepValid}
            extraParams={extraParams}
            onCustomSubmit={isChangeOrder ? submitChangeOrder : null}
            submitLabel={submitLabel}
            headerTitle={headerTitle}
            headerSubtitle={headerSubtitle}
            successTitle={successTitle}
            successMessage={successMessage}
            onSuccess={onSuccess}
            onClose={onClose}
        />
    );
};

export default RehireTalentModal;